import React, { useState } from "react";
import { auth, db } from "./firebase/firebaseConfig";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import logoEN from "./assets/plogoE.png";
import logoAR from "./assets/plogoA.png";
import { useTranslation } from "react-i18next";
import { useBanner } from "./components/BannerContext";

function Login({ setUser }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loginError, setLoginError] = useState(false);
  const { showBanner } = useBanner();

  const handleForgotPassword = async () => {
  if (!email) {
    showBanner(t("enter_email"), "error");
    return;
  }

  try {
    await sendPasswordResetEmail(auth, email);
    showBanner(t("reset_sent"), "success");
  } catch (error) {
    console.error("🔴 Forgot password error:", error);
    showBanner(t("error_occurred"), "error");
  }
};

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      console.log("✅ Authenticated user UID:", user.uid);

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log("📄 Fetched Firestore user data:", userData);

        setUser({ ...user, name: userData.name, role: userData.role, club: userData.club });

        // ✅ Clear error
        setLoginError("");

        if (userData.role === "therapist") navigate("/therapist-dashboard");
        else if (userData.role === "athlete") navigate("/athlete-dashboard");
        else if (userData.role === "client") navigate("/athlete-dashboard");
        else if (userData.role === "coach") navigate("/coach-dashboard");
        else if (userData.role === "admin") navigate("/admin");
        else if (userData.role === "nutritionist") navigate("/nutritionist-dashboard");
      } else {
        console.error("❌ No Firestore document found for UID:", user.uid);
        showBanner(t("no_role"), "error");
      }
    } catch (error) {
      console.error("❌ Login error:", error);
      setLoginError(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <div className="w-full max-w-md bg-[#f1efe7] rounded-xl shadow-lg p-8">
        <div className="flex justify-end mb-4">
          <button
            onClick={() => i18n.changeLanguage(i18n.language === "ar" ? "en" : "ar")}
            className="text-sm text-[#b22222] border border-[#b22222] px-3 py-1 rounded hover:bg-[#b22222] hover:text-white transition"
          >
            {i18n.language === "ar" ? "English" : "العربية"}
          </button>
        </div>

        <div className="flex flex-col items-center mb-6">
          <img
            src={i18n.language === "ar" ? logoAR : logoEN}
            alt="ath.ar advisory logo"
            className="h-16"
          />
          <h1 className="text-2xl font-bold text-gray-800">{t("welcome")}</h1>
          <p className="text-sm text-gray-500">{t("sign_in_to_continue")}</p>
        </div>
        {loginError && (
          <p className="text-red-600 text-sm mt-2 text-center">{loginError}</p>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
        {loginError && (
            <div
              className={`flex ${i18n.language === "ar" ? "justify-start" : "justify-end"}`}
            >
              <p className="text-sm text-[#b22222] mt-1">{t("login_failed")}</p>
            </div>
          )}

          <input
            type="email"
            placeholder={t("email")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (loginError) setLoginError(""); // ✅ optional
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#b22222]"
            required
          />

          <input
            type="password"
            placeholder={t("password")}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (loginError) setLoginError(""); // ✅ optional
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#b22222]"
            required
          />
          
          <div className="flex justify-end">
            <button
              type="button"
              className="text-sm text-[#b22222] hover:underline"
              onClick={handleForgotPassword}
            >
              {t("forgot_password")}
            </button>
          </div>

          <button
            type="submit"
            className="w-full bg-[#b22222] hover:bg-red-800 text-white py-2 rounded-md font-semibold transition"
          >
            {t("login")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
